import * as React from "react";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { styled } from "@mui/material/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { showModal } from "../redux/actions/ModalActions";
import { useDispatch } from "react-redux";
import { setId } from "../redux/actions/IdAction";
import BlockIcon from "@mui/icons-material/Block";
import { Link } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Switch } from "@mui/material";

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => onPageChange(event, 0);
  const handleBackButtonClick = (event) => onPageChange(event, page - 1);
  const handleNextButtonClick = (event) => onPageChange(event, page + 1);
  const handleLastPageButtonClick = (event) =>
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0}>
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0}>
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

export default function DataTable({
  rows,
  columns,
  status,
  showActions = true,
}) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  // Reset to page 0 whenever search-filtered data changes
  React.useEffect(() => {
    setPage(0);
  }, [rows]);

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette?.common.black,
      color: theme.palette?.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": { backgroundColor: theme.palette?.action.hover },
    "&:last-child td, &:last-child th": { border: 0 },
  }));

  const dispatch = useDispatch();
  const storeId = (modal, id, role, status) => {
    let data = { _id: id, status: !status };
    const api =
      role == 2 ? "changeStatusTrainer" : role == 3 && "changeStatusStudent";
    dispatch(showModal(modal, data, api, status ? "block" : "unblock"));
    dispatch(setId(id));
  };

  const userRole = sessionStorage.getItem("userRole");
  const path = userRole == 1 ? "admin" : "trainer";

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 500 }} aria-label="custom pagination table">
        <TableHead>
          <TableRow>
            <StyledTableCell>Sr No.</StyledTableCell>
            {columns?.map((el, index) => (
              <StyledTableCell key={index}>{el?.headerName}</StyledTableCell>
            ))}
            {showActions && <StyledTableCell>Status</StyledTableCell>}
            {showActions && <StyledTableCell>Actions</StyledTableCell>}
          </TableRow>
        </TableHead>

        <TableBody>
          {console.log(rows.length)}
          {rows.length > 0 ? (
            (rowsPerPage > 0
              ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              : rows
            ).map((row, index) => (
              <StyledTableRow hover key={index}>
                {console.log(rows)}

                <StyledTableCell>
                  {page * rowsPerPage + index + 1}
                </StyledTableCell>
                {columns.map((col, i) => {
                  if (col.fields === "batchesId") {
                    return (
                      <StyledTableCell key={i}>
                        {row?.batchesId?.length > 0 ? (
                          row.batchesId.slice(0, 3).map((batch, idx) => (
                            <div key={idx} className="  me-1">
                              {batch.name}
                            </div>
                          ))
                        ) : (
                          <span className="text-muted">No Batch</span>
                        )}
                      </StyledTableCell>
                    );
                  }

                  if (col.fields === "createdAt") {
                    return (
                      <StyledTableCell key={i}>
                        {new Date(row[col.fields]).toLocaleDateString("en-IN")}
                      </StyledTableCell>
                    );
                  }

                  return (
                    <StyledTableCell key={i}>{row[col.fields]}</StyledTableCell>
                  );
                })}

                {showActions && (
                  <>
                    <StyledTableCell>
                      <div className="d-flex">
                        {status == 1 ? (
                          // <IconButton
                          //   className="fs-6 fw-bold text-danger"

                          //   >
                          //   </IconButton>
                          <Switch
                            checked={row?.userId?.status}
                            onChange={() =>
                              storeId(
                                "StatusModal",
                                row?.userId?._id,
                                row?.userId?.role,
                                row?.userId?.status
                              )
                            }
                            defaultChecked
                          />
                        ) : (
                          // <IconButton
                          //   className="fs-6 fw-bold text-success"
                          //   onClick={() =>
                          //     storeId("StatusModal", row?.userId?._id, row?.userId?.role, row?.userId?.status)
                          //   }
                          // >
                          //   <CheckCircleOutlineIcon className="fs-6 fw-bold" /> Un55-Block
                          // </IconButton>
                          <Switch
                            checked={row?.userId?.status}
                            onChange={() =>
                              storeId(
                                "StatusModal",
                                row?.userId?._id,
                                row?.userId?.role,
                                row?.userId?.status
                              )
                            }
                            defaultChecked
                          />
                        )}
                      </div>
                    </StyledTableCell>

                    <StyledTableCell>
                      <div className="dropdown">
                        <IconButton data-bs-toggle="dropdown">
                          <MoreVertIcon />
                        </IconButton>
                        <ul className="dropdown-menu">
                          <li>
                            <a
                              href="#"
                              onClick={() =>
                                dispatch(
                                  showModal("EditPeople", {
                                    _id:
                                      row?.userId?.role === 2
                                        ? row?._id
                                        : row?.userId?._id,
                                    role: row?.userId?.role,
                                  })
                                )
                              }
                              className="dropdown-item"
                            >
                              Edit
                            </a>
                          </li>
                          <li>
                            <a
                              href="#"
                              onClick={() =>
                                storeId("ChangePassword", row?.userId?._id)
                              }
                              className="dropdown-item"
                            >
                              Change Password
                            </a>
                          </li>
                          {userRole == 1 && row?.userId?.role == 2 && (
                            <li>
                              <Link
                                to={`/${path}/batches/${
                                  row.userId?.role == 2
                                    ? "trainer/"
                                    : "student/"
                                }${row?.userId?._id}`}
                                className="dropdown-item"
                              >
                                View Batches
                              </Link>
                            </li>
                          )}
                        </ul>
                      </div>
                    </StyledTableCell>
                  </>
                )}
              </StyledTableRow>
            ))
          ) : (
            <>
              <StyledTableRow hover>
                <StyledTableCell
                  className="text-center"
                  colSpan={columns?.length + (showActions ? 2 : 1)}
                >
                  No data Found{" "}
                </StyledTableCell>
              </StyledTableRow>
            </>
          )}

          {emptyRows > 0 && (
            <StyledTableRow style={{ height: 53 * emptyRows }}>
              <StyledTableCell
                colSpan={columns?.length + (showActions ? 2 : 1)}
              />
              hlo
            </StyledTableRow>
          )}
        </TableBody>

        {rows.length > 0 && (
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                colSpan={columns?.length + (showActions ? 2 : 1)}
                count={rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
}
